import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ConsentPreferences {
  analytics: boolean;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  private readonly CONSENT_KEY = 'cookie_consent_preferences';
  private readonly CONSENT_BANNER_DISMISSED_KEY = 'cookie_consent_banner_dismissed';
  
  // Subject to notify when consent changes
  private consentSubject = new BehaviorSubject<ConsentPreferences | null>(this.getStoredConsent());
  
  constructor() {
    console.log('[CookieConsent] Service initialized');
  }

  /**
   * Observable that emits when consent preferences change
   */
  get consent$(): Observable<ConsentPreferences | null> {
    return this.consentSubject.asObservable();
  }

  /**
   * Check if user is likely from EU/EEA/UK/Switzerland
   * This uses timezone as a heuristic - not perfect but simple
   */
  isLikelyFromEU(): boolean {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Common EU/EEA/UK/Switzerland timezones
      const euTimezones = [
        'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome',
        'Europe/Madrid', 'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Vienna',
        'Europe/Stockholm', 'Europe/Copenhagen', 'Europe/Oslo', 'Europe/Helsinki',
        'Europe/Warsaw', 'Europe/Prague', 'Europe/Budapest', 'Europe/Bucharest',
        'Europe/Sofia', 'Europe/Athens', 'Europe/Dublin', 'Europe/Lisbon',
        'Europe/Zurich', 'Europe/Luxembourg', 'Europe/Malta', 'Europe/Tallinn',
        'Europe/Riga', 'Europe/Vilnius', 'Europe/Zagreb', 'Europe/Ljubljana',
        'Europe/Bratislava', 'Europe/Nicosia', 'Atlantic/Reykjavik'
      ];
      
      return euTimezones.some(tz => timezone.startsWith(tz.split('/')[0]));
    } catch (error) {
      console.warn('[CookieConsent] Failed to detect timezone, defaulting to requiring consent', error);
      return true; // Default to requiring consent if we can't determine
    }
  }

  /**
   * Check if consent is required (EU user and no consent given yet)
   */
  isConsentRequired(): boolean {
    // If user has already given/denied consent, don't require again
    if (this.hasConsentDecision()) {
      return false;
    }
    
    // Otherwise, check if user is from EU
    return this.isLikelyFromEU();
  }

  /**
   * Check if user has made a consent decision
   */
  hasConsentDecision(): boolean {
    return this.getStoredConsent() !== null || this.isBannerDismissed();
  }

  /**
   * Check if consent banner was dismissed (rejected)
   */
  isBannerDismissed(): boolean {
    return localStorage.getItem(this.CONSENT_BANNER_DISMISSED_KEY) === 'true';
  }

  /**
   * Get stored consent preferences
   */
  getStoredConsent(): ConsentPreferences | null {
    try {
      const stored = localStorage.getItem(this.CONSENT_KEY);
      if (stored) {
        return JSON.parse(stored) as ConsentPreferences;
      }
    } catch (error) {
      console.error('[CookieConsent] Failed to parse stored consent', error);
    }
    return null;
  }

  /**
   * Check if analytics consent is granted
   */
  hasAnalyticsConsent(): boolean {
    const consent = this.getStoredConsent();
    return consent?.analytics === true;
  }

  /**
   * Grant analytics consent
   */
  grantConsent(): void {
    const preferences: ConsentPreferences = {
      analytics: true,
      timestamp: Date.now(),
    };
    
    localStorage.setItem(this.CONSENT_KEY, JSON.stringify(preferences));
    localStorage.removeItem(this.CONSENT_BANNER_DISMISSED_KEY);
    
    console.log('[CookieConsent] Consent granted');
    this.consentSubject.next(preferences);
  }

  /**
   * Deny analytics consent (dismiss banner)
   */
  denyConsent(): void {
    localStorage.setItem(this.CONSENT_BANNER_DISMISSED_KEY, 'true');
    localStorage.removeItem(this.CONSENT_KEY);
    
    console.log('[CookieConsent] Consent denied');
    this.consentSubject.next(null);
  }

  /**
   * Revoke consent (for user settings/privacy page)
   */
  revokeConsent(): void {
    localStorage.removeItem(this.CONSENT_KEY);
    localStorage.removeItem(this.CONSENT_BANNER_DISMISSED_KEY);
    
    console.log('[CookieConsent] Consent revoked');
    this.consentSubject.next(null);
  }

  /**
   * Check if banner should be shown
   */
  shouldShowBanner(): boolean {
    // Don't show if user has already made a decision
    if (this.hasConsentDecision()) {
      return false;
    }
    
    // Show if user is from EU
    return this.isLikelyFromEU();
  }
}

